import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";

import { can, podeAcessarSaude } from "./auth/permissions";
import PrivateShell from "./layouts/PrivateShell";
import { ThemeProvider } from "./theme/ThemeContext";

const Extrusao = lazy(() => import("./Producao/Extrusao"));
const Impressao = lazy(() => import("./Producao/Impressao"));
const Corte = lazy(() => import("./Producao/Corte"));
const Expedicao = lazy(() => import("./Producao/Expedicao"));
const Login = lazy(() => import("./auth/Login"));
const CadastroUsuario = lazy(() => import("./auth/CadastroUsuario"));
const Pedidos = lazy(() => import("./Financeiro/Pedidos"));
const Relatorios = lazy(() => import("./Financeiro/Relatorios"));
const Financeiro = lazy(() => import("./Financeiro/Financeiro"));
const FinanceiroHub = lazy(() => import("./Financeiro/FinanceiroHub"));
const RelatorioFinanceiro = lazy(() => import("./Financeiro/RelatorioFinanceiro"));
const NotasFiscais = lazy(() => import("./Estoque/NotasFiscais"));
const Dashboard = lazy(() => import("./Dashboard/Dashboard"));
const Estoque = lazy(() => import("./Estoque/Estoque"));
const ControleEstoque = lazy(() => import("./Estoque/ControleEstoque"));
const Fornecedores = lazy(() => import("./Estoque/Fornecedores"));
const OrcamentosVendas = lazy(() => import("./pages/OrcamentosVendas"));
const PedidosVendas = lazy(() => import("./vendas/pedidos/PedidosVendas"));
const Vendedores = lazy(() => import("./pages/Vendedores"));
const ConfiguracaoEmpresa = lazy(() => import("./pages/ConfiguracaoEmpresa"));
const ContasReceber = lazy(() => import("./pages/ContasReceber"));
const VendaDetalhe = lazy(() => import("./pages/VendaDetalhe"));
const OrcamentoComissao = lazy(() => import("./pages/OrcamentoComissao"));
const ProdutosVenda = lazy(() => import("./pages/ProdutosVenda"));
const RelatorioVendas = lazy(() => import("./pages/RelatorioVendas"));
const PlanilhasAuxiliares = lazy(() => import("./pages/PlanilhasAuxiliares"));
const CustosProducao = lazy(() => import("./pages/CustosProducao"));
const SaudeSistema = lazy(() => import("./pages/SaudeSistema"));
const RecursosHumanos = lazy(() => import("./GestaoDePessoas/RecursosHumanos"));
const DepartamentoPessoal = lazy(() => import("./GestaoDePessoas/DepartamentoPessoal"));
const ControlePonto = lazy(() => import("./GestaoDePessoas/ControlePonto"));
const PontoFuncionario = lazy(() => import("./GestaoDePessoas/PontoFuncionario"));
const ContasPagar = lazy(() => import("./Financeiro/ContasPagar"));
const Suprimentos = lazy(() => import("./Estoque/Suprimentos"));
const ShopeeShell = lazy(() => import("./shopee/ShopeeShell"));
const ShopeeDashboard = lazy(() => import("./shopee/ShopeeDashboard"));
const ShopeeProdutosPage = lazy(() => import("./shopee/ShopeeProdutosPage"));
const ShopeeVendasPage = lazy(() => import("./shopee/ShopeeVendasPage"));
const ShopeeEstoquePage = lazy(() => import("./shopee/ShopeeEstoquePage"));
const ShopeeRelatorioVendasPage = lazy(() => import("./shopee/ShopeeRelatorioVendasPage"));
const ShopeeContaCorrenteFornecedoresPage = lazy(() => import("./shopee/ShopeeContaCorrenteFornecedoresPage"));
const ShopeeNotasFiscaisPage = lazy(() => import("./shopee/ShopeeNotasFiscaisPage"));
const ShopeeApiPage = lazy(() => import("./shopee/ShopeeApiPage"));
const MercadoLivreConfigPage = lazy(() => import("./mercadolivre/MercadoLivreConfigPage"));
const MercadoLivreCatalogoPage = lazy(() => import("./mercadolivre/MercadoLivreCatalogoPage"));

function LoadingRoute() {
  return (
    <div style={{ padding: 24, color: "#475569", fontSize: 14 }}>
      Carregando...
    </div>
  );
}

function RequireSaude({ usuario, children }) {
  if (!usuario) return <Navigate to="/" replace />;
  if (usuario.area === "shopee") return <Navigate to="/shopee" replace />;
  if (!podeAcessarSaude(usuario)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequirePerm({ usuario, perm, children }) {
  if (!usuario) return <Navigate to="/" replace />;
  if (usuario.area === "shopee") return <Navigate to="/shopee" replace />;
  if (!can(usuario, perm)) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequireMainArea({ usuario, children }) {
  if (!usuario) return <Navigate to="/" replace />;
  if (usuario.area === "shopee") return <Navigate to="/shopee" replace />;
  return children;
}

function RequireShopeeArea({ usuario, children }) {
  if (!usuario) return <Navigate to="/" replace />;
  if (!can(usuario, "shopee")) return <Navigate to="/dashboard" replace />;
  if (usuario.area !== "shopee") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppInner() {
  const [usuarioLogado, setUsuarioLogado] = useState(() => {
    const raw = localStorage.getItem("usuarioLogado") || sessionStorage.getItem("usuarioLogado");
    if (!raw) return "";
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  });

  const navigate = useNavigate();

  // Atualiza os acessos da sessao aberta com o cadastro atual (superAdmin,
  // permissoes e extras), sem precisar sair e entrar de novo.
  const usuarioId = typeof usuarioLogado === "object" && usuarioLogado ? usuarioLogado.id : usuarioLogado;
  useEffect(() => {
    if (!usuarioId) return undefined;
    const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) return undefined;
    let cancelado = false;
    const baseURL = window.location.port === "3000"
      ? window.location.origin.replace(":3000", ":3001")
      : `${window.location.origin}/api`;
    fetch(`${baseURL}/usuarios/me/acesso`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((acesso) => {
        if (cancelado || !acesso || !acesso.id) return;
        setUsuarioLogado((atual) => {
          const base = typeof atual === "object" && atual ? atual : {};
          const proximo = {
            ...base,
            id: acesso.id,
            nome: acesso.nome,
            usuario: acesso.usuario,
            permissoes: Array.isArray(acesso.permissoes) ? acesso.permissoes : [],
            permissoesExtras: Array.isArray(acesso.permissoesExtras) ? acesso.permissoesExtras : [],
            superAdmin: Number(acesso.superAdmin) === 1 ? 1 : 0,
            role: acesso.role || base.role || "admin",
            vendedorId: acesso.vendedorId || null,
            shopeeContaId: acesso.shopeeContaId || null,
          };
          if (JSON.stringify(proximo) === JSON.stringify(base)) return atual;
          const txt = JSON.stringify(proximo);
          if (localStorage.getItem("usuarioLogado")) localStorage.setItem("usuarioLogado", txt);
          if (sessionStorage.getItem("usuarioLogado")) sessionStorage.setItem("usuarioLogado", txt);
          return proximo;
        });
      })
      .catch(() => null);
    return () => { cancelado = true; };
  }, [usuarioId]);

  const handleLogout = async () => {
    const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || localStorage.getItem("token") || sessionStorage.getItem("token");
    if (token) {
      const baseURL = window.location.port === "3000"
        ? window.location.origin.replace(":3000", ":3001")
        : `${window.location.origin}/api`;
      await fetch(`${baseURL}/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => null);
    }
    localStorage.removeItem("usuarioLogado");
    localStorage.removeItem("lastActivityAt");
    sessionStorage.removeItem("usuarioLogado");
    localStorage.removeItem("authToken");
    localStorage.removeItem("authTokenExpiresAt");
    localStorage.removeItem("token");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("authTokenExpiresAt");
    sessionStorage.removeItem("lastActivityAt");
    setUsuarioLogado("");
    navigate("/", { replace: true });
  };

  return (
    <Routes>
      <Route path="/" element={<Login setUsuarioLogado={setUsuarioLogado} usuarioLogado={usuarioLogado} />} />

      <Route
        path="/shopee"
        element={
          <RequireShopeeArea usuario={usuarioLogado}>
            <ShopeeShell usuario={usuarioLogado} onLogout={handleLogout} />
          </RequireShopeeArea>
        }
      >
        <Route index element={<Navigate to="produtos" replace />} />
        <Route path="produtos" element={<ShopeeProdutosPage />} />
        <Route path="vendas" element={<ShopeeVendasPage />} />
        <Route path="estoque" element={<ShopeeEstoquePage />} />
        <Route path="relatorio-vendas" element={<ShopeeRelatorioVendasPage />} />
        <Route path="conta-corrente" element={<ShopeeContaCorrenteFornecedoresPage />} />
        <Route path="notas-fiscais" element={<ShopeeNotasFiscaisPage />} />
        <Route path="api" element={<ShopeeApiPage />} />
        <Route path="inicio" element={<ShopeeDashboard usuario={usuarioLogado} />} />
      </Route>

      <Route
        path="/mercadolivre"
        element={
          <RequireShopeeArea usuario={usuarioLogado}>
            <ShopeeShell usuario={usuarioLogado} onLogout={handleLogout} />
          </RequireShopeeArea>
        }
      >
        <Route index element={<Navigate to="catalogo" replace />} />
        <Route path="catalogo" element={<MercadoLivreCatalogoPage />} />
        <Route path="config" element={<MercadoLivreConfigPage />} />
      </Route>

      <Route
        element={
          <RequireMainArea usuario={usuarioLogado}>
            <PrivateShell usuario={usuarioLogado} onLogout={handleLogout} />
          </RequireMainArea>
        }
      >
        <Route path="/dashboard" element={<Dashboard usuario={usuarioLogado} />} />
        <Route path="/extrusao" element={<RequirePerm usuario={usuarioLogado} perm="extrusao"><Extrusao /></RequirePerm>} />
        <Route path="/impressao" element={<RequirePerm usuario={usuarioLogado} perm="impressao"><Impressao /></RequirePerm>} />
        <Route path="/corte" element={<RequirePerm usuario={usuarioLogado} perm="corte"><Corte /></RequirePerm>} />
        <Route path="/expedicao" element={<RequirePerm usuario={usuarioLogado} perm="expedicao"><Expedicao /></RequirePerm>} />
        <Route path="/pedidos" element={<RequirePerm usuario={usuarioLogado} perm="pedidos"><Pedidos /></RequirePerm>} />
        <Route path="/relatorios" element={<RequirePerm usuario={usuarioLogado} perm="relatorios"><Relatorios /></RequirePerm>} />
        <Route path="/financeiro" element={<RequirePerm usuario={usuarioLogado} perm="financeiro"><FinanceiroHub /></RequirePerm>} />
        <Route path="/financeiro/legado" element={<RequirePerm usuario={usuarioLogado} perm="financeiro"><Financeiro /></RequirePerm>} />
        <Route path="/financeiro/contas-pagar" element={<RequirePerm usuario={usuarioLogado} perm="financeiro"><ContasPagar /></RequirePerm>} />
        <Route path="/contas-pagar" element={<RequirePerm usuario={usuarioLogado} perm="financeiro"><ContasPagar /></RequirePerm>} />
        <Route path="/relatorio-financeiro" element={<RequirePerm usuario={usuarioLogado} perm="relatorioFinanceiro"><RelatorioFinanceiro /></RequirePerm>} />
        <Route path="/orcamento-comissao" element={<RequirePerm usuario={usuarioLogado} perm="financeiro"><OrcamentoComissao /></RequirePerm>} />
        <Route path="/cadastro-usuario" element={<RequirePerm usuario={usuarioLogado} perm="cadastroUsuario"><CadastroUsuario usuarioLogado={usuarioLogado} /></RequirePerm>} />
        <Route path="/saude-sistema" element={<RequireSaude usuario={usuarioLogado}><SaudeSistema /></RequireSaude>} />
        <Route path="/vendedores" element={<Vendedores />} />
        <Route path="/configuracao-empresa" element={<RequirePerm usuario={usuarioLogado} perm="cadastroUsuario"><ConfiguracaoEmpresa /></RequirePerm>} />
        <Route path="/custos" element={<RequirePerm usuario={usuarioLogado} perm={can(usuarioLogado, "cadastroUsuario") ? "cadastroUsuario" : "custos"}><CustosProducao /></RequirePerm>} />
        <Route path="/estoque" element={<RequirePerm usuario={usuarioLogado} perm="estoque"><Estoque /></RequirePerm>} />
        <Route path="/controle-estoque" element={<RequirePerm usuario={usuarioLogado} perm="estoque"><ControleEstoque /></RequirePerm>} />
        <Route path="/cadastro-fornecedores" element={<RequirePerm usuario={usuarioLogado} perm="estoque"><Fornecedores /></RequirePerm>} />
        <Route path="/fornecedores" element={<Fornecedores />} />
        <Route path="/suprimentos" element={<RequirePerm usuario={usuarioLogado} perm="estoque"><Suprimentos /></RequirePerm>} />
        <Route path="/produtos-venda" element={<RequirePerm usuario={usuarioLogado} perm="estoque"><ProdutosVenda /></RequirePerm>} />
        <Route path="/notasfiscais" element={<RequirePerm usuario={usuarioLogado} perm="notasfiscais"><NotasFiscais /></RequirePerm>} />
        <Route path="/notasfiscais-lab" element={<Navigate to="/notasfiscais" replace />} />
        <Route path="/orcamentos" element={<RequirePerm usuario={usuarioLogado} perm="vendas"><OrcamentosVendas /></RequirePerm>} />
        <Route path="/vendas" element={<RequirePerm usuario={usuarioLogado} perm="vendas"><PedidosVendas /></RequirePerm>} />
        <Route path="/contas-receber" element={<RequirePerm usuario={usuarioLogado} perm="financeiro"><ContasReceber /></RequirePerm>} />
        <Route path="/contas-receber/venda/:id" element={<RequirePerm usuario={usuarioLogado} perm="financeiro"><VendaDetalhe /></RequirePerm>} />
        <Route path="/relatorio-vendas" element={<RequirePerm usuario={usuarioLogado} perm="vendas"><RelatorioVendas /></RequirePerm>} />
        <Route path="/planilhas-auxiliares" element={<RequirePerm usuario={usuarioLogado} perm="planilhasAuxiliares"><PlanilhasAuxiliares /></RequirePerm>} />
        <Route path="/recursos-humanos" element={<RequirePerm usuario={usuarioLogado} perm="rh"><RecursosHumanos usuario={usuarioLogado} /></RequirePerm>} />
        <Route path="/departamento-pessoal" element={<RequirePerm usuario={usuarioLogado} perm="dp"><DepartamentoPessoal usuario={usuarioLogado} /></RequirePerm>} />
        <Route path="/controle-ponto" element={<RequirePerm usuario={usuarioLogado} perm="ponto"><ControlePonto usuario={usuarioLogado} /></RequirePerm>} />
        <Route path="/ponto/funcionario/:id" element={<PontoFuncionario />} />
      </Route>

      <Route path="*" element={<Navigate to={usuarioLogado?.area === "shopee" ? "/shopee" : usuarioLogado ? "/dashboard" : "/"} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <Router>
        <Suspense fallback={<LoadingRoute />}>
          <AppInner />
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}







